// src/views/bots/flow/nodes/TemplateNode.tsx
// Dedicated node for WhatsApp template messages in the bot flow builder

import React, { memo, useState, useEffect } from 'react';
import { Position, useReactFlow } from '@xyflow/react';
import * as ReactFlowComponents from '@xyflow/react';
import styles from './nodes.module.css';
import UniversalTemplateSelector from '../../../createWhatsappBroadcast/UnifiedTemplateSelector';
import { getTemplateById } from '../../../../services/templates';
import logger from '../../../../utils/logger';

// Workaround for @xyflow/react v12 type collision
const Handle = ReactFlowComponents.Handle as React.ComponentType<{
  type: 'source' | 'target';
  position: typeof Position[keyof typeof Position];
  id: string;
  className?: string;
  style?: React.CSSProperties;
}>;

interface TemplateNodeProps {
  id: string;
  data: {
    questionType: string;
    templateData?: {
      templateId: string;
      templateName: string;
      template?: any;
    };
    groupId?: string;
    stepId?: string;
  };
  selected?: boolean;
}

const TemplateNode: React.FC<TemplateNodeProps> = ({ id, data, selected = false }) => {
  const [showSelector, setShowSelector] = useState(false);
  const [loadedTemplate, setLoadedTemplate] = useState<any>(data.templateData?.template || null);
  const [isLoading, setIsLoading] = useState(false);
  const { setNodes, setEdges } = useReactFlow();

  const templateId = data.templateData?.templateId;
  const templateName = data.templateData?.templateName || loadedTemplate?.name || '';

  // Lazy-load the full template object when we have an ID but no object yet
  useEffect(() => {
    if (templateId && !loadedTemplate) {
      setIsLoading(true);
      getTemplateById(templateId)
        .then((res) => {
          const tpl = res.data?.data || res.data;
          setLoadedTemplate(tpl);
          // Patch node data so name is accurate
          updateNodeData({
            templateData: {
              templateId,
              templateName: tpl?.name || templateName,
              template: tpl,
            },
          });
        })
        .catch((err) => {
          logger.warn('[TemplateNode] Failed to load template:', err);
        })
        .finally(() => setIsLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // Sync if data changes externally (e.g. undo/redo)
  useEffect(() => {
    if (data.templateData?.template) {
      setLoadedTemplate(data.templateData.template);
    }
  }, [data.templateData?.template]);

  const updateNodeData = (updates: any) => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, ...updates } } : node
      )
    );
  };

  const handleTemplateSelect = (template: any) => {
    logger.log('[TemplateNode] Template selected:', template.name);
    setLoadedTemplate(template);
    updateNodeData({
      templateData: {
        templateId: template._id || template.id,
        templateName: template.name,
        template,
      },
    });
    setShowSelector(false);
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes((nodes) => {
      const original = nodes.find((n) => n.id === id);
      if (!original) return nodes;
      const newId = `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      return [
        ...nodes,
        {
          ...original,
          id: newId,
          position: { x: original.position.x + 50, y: original.position.y + 50 },
          selected: false,
          data: { ...original.data, stepId: newId },
        },
      ];
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEdges((edges) => edges.filter((e) => e.source !== id && e.target !== id));
    setNodes((nodes) => nodes.filter((n) => n.id !== id));
  };

  // Extract a short body preview from the template object
  const getBodyPreview = (tpl: any): string => {
    if (!tpl) return '';
    const body =
      tpl.body ||
      tpl.components?.find((c: any) => c.type === 'BODY')?.text ||
      '';
    return body.length > 80 ? body.slice(0, 80) + '…' : body;
  };

  const bodyPreview = getBodyPreview(loadedTemplate);
  const hasTemplate = !!templateId;

  return (
    <div
      className={`${styles.whatsappMessage} ${selected ? styles.selected : ''} ${selected ? styles.expanded : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        id="question-input"
        className={styles.handle}
      />

      {/* Copy / Delete */}
      <div className={styles.nodeActions}>
        <button className={styles.copyButton} onClick={handleCopy} title="Copy node">📋</button>
        <button className={styles.deleteButton} onClick={handleDelete} title="Delete node">🗑️</button>
      </div>

      {/* WhatsApp bubble */}
      <div className={styles.messageBubble}>

        {/* Type badge */}
        <div className={styles.messageTypeBadge} style={{ background: '#6366f1' }}>
          <span className={styles.messageTypeIcon}>📄</span>
          <span className={styles.messageTypeLabel}>Template Message</span>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className={styles.messageText} style={{ color: '#667781', fontStyle: 'italic' }}>
            Loading template…
          </div>
        ) : hasTemplate && loadedTemplate ? (
          /* Selected state */
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 6,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#111b21',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {templateName}
              </span>
              {selected && (
                <button
                  style={{
                    fontSize: 9,
                    padding: '2px 6px',
                    background: 'rgba(99,102,241,0.1)',
                    border: '1px solid rgba(99,102,241,0.3)',
                    borderRadius: 4,
                    color: '#6366f1',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSelector(true);
                  }}
                >
                  Change
                </button>
              )}
            </div>
            {bodyPreview && (
              <div
                style={{
                  fontSize: 11,
                  color: '#374151',
                  lineHeight: '14px',
                  fontStyle: 'italic',
                  marginBottom: 2,
                }}
              >
                {bodyPreview}
              </div>
            )}
          </div>
        ) : hasTemplate && !loadedTemplate ? (
          /* Has ID but object not loaded yet (and not loading — edge case) */
          <div className={styles.messageText} style={{ color: '#667781', fontStyle: 'italic' }}>
            Template: {templateName || templateId}
          </div>
        ) : (
          /* Empty state */
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              padding: '8px 0 4px',
              cursor: selected ? 'pointer' : 'default',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (selected) setShowSelector(true);
            }}
          >
            <span style={{ fontSize: 18 }}>📄</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: selected ? '#6366f1' : '#9ca3af',
              }}
            >
              {selected ? 'Click to select template' : 'No template selected'}
            </span>
            {!selected && (
              <span style={{ fontSize: 9, color: '#9ca3af' }}>Click node to edit</span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className={styles.messageFooter}>
          <span className={styles.messageTime}>Bot</span>
        </div>
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="question-output"
        className={styles.handle}
        style={{ background: '#6366f1' }}
      />

      {/* Template selector modal */}
      {showSelector && (
        <UniversalTemplateSelector
          isOpen={showSelector}
          onClose={() => setShowSelector(false)}
          onTemplateSelect={handleTemplateSelect}
          selectedTemplateId={loadedTemplate?._id}
          templateType="whatsapp"
          handleDiscard={() => setShowSelector(false)}
        />
      )}
    </div>
  );
};

export default memo(TemplateNode);
